import { useState, useCallback } from 'react'
import { rewriteApi } from '@/services/api'

interface UseImageUploadOptions {
  projectId?: string
  projectTitle: string
  createProject: (title: string) => Promise<{ id: string } | null>
  onTextExtracted: (content: string, title?: string) => void
}

export function useImageUpload({ projectId, projectTitle, createProject, onTextExtracted }: UseImageUploadOptions) {
  const [uploadingImage, setUploadingImage] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [imageMethod, setImageMethod] = useState<'api' | 'ocr'>('api')
  const [imageModel, setImageModel] = useState('gemini-2.5-flash')
  const [pendingImage, setPendingImage] = useState<{ file: File; previewUrl: string } | null>(null)

  const handleImageSelected = useCallback((file: File) => {
    const previewUrl = URL.createObjectURL(file)
    setPendingImage({ file, previewUrl })
  }, [])

  const handleImageConfirm = useCallback(async () => {
    if (!pendingImage) return
    setUploadingImage(true)
    setPendingImage(null)
    let pid = projectId
    if (!pid) {
      const project = await createProject(projectTitle || '새 프로젝트')
      if (!project) { setUploadingImage(false); return }
      pid = project.id
    }
    const res = await rewriteApi.sources.uploadImage(pid, pendingImage.file, { method: imageMethod, model: imageModel })
    if (res.success && res.data) {
      onTextExtracted(res.data.content, res.data.title || undefined)
    }
    setUploadingImage(false)
  }, [pendingImage, projectId, projectTitle, imageMethod, imageModel, createProject, onTextExtracted])

  const handleImageCancel = useCallback(() => {
    if (pendingImage) {
      URL.revokeObjectURL(pendingImage.previewUrl)
      setPendingImage(null)
    }
  }, [pendingImage])

  return {
    uploadingImage,
    dragOver,
    setDragOver,
    imageMethod,
    setImageMethod,
    imageModel,
    setImageModel,
    pendingImage,
    handleImageSelected,
    handleImageConfirm,
    handleImageCancel,
  }
}
